import React, { useState, useEffect } from "react";
import DatePeriodButtons from "../Shared/DatePeriodButtons";
import TransactionDetails from "./TransactionDetails";
import { Transaction } from "../../types";

interface TransactionListProps {
  transactions: Transaction[];
  loading?: boolean;
  onRefresh?: () => void;
  startDate?: string;
  endDate?: string;
  onDateFilterChange?: (startDate: string, endDate: string) => void;
  onAddTransaction?: () => void;
}

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  loading = false,
  onRefresh,
  startDate = new Date().toISOString().split("T")[0],
  endDate = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  })(),
  onDateFilterChange,
  onAddTransaction,
}) => {
  const saleFilterValue = "sale";
  const refundFilterValue = "refund";

  const [filter, setFilter] = useState<
    "all" | typeof saleFilterValue | typeof refundFilterValue
  >("all");
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("1mth");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Check if it's mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const filteredTransactions = transactions.filter((transaction) => {
    // Filter by transaction type
    if (filter !== "all" && transaction.transType !== filter) {
      return false;
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const searchFields = [
        transaction.invoice,
        transaction.transactionId,
        transaction.account,
        transaction.errorText,
      ];

      const matchesSearch = searchFields.some((field) =>
        field?.toLowerCase().includes(searchLower)
      );

      if (!matchesSearch) {
        return false;
      }
    }

    return true;
  });

  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  const handleDateFilterApply = () => {
    if (new Date(localStartDate) > new Date(localEndDate)) {
      alert("Start date cannot be after end date");
      return;
    }
    setSelectedPeriod("Custom");
    if (onDateFilterChange) {
      onDateFilterChange(localStartDate, localEndDate);
    }
  };

  const periodDays: Record<string, number> = {
    "1wk": 7,
    "1mth": 30,
    "3mth": 90,
    "6mth": 180,
    "1yr": 365,
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    const days = periodDays[period];
    if (days == null) return;

    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);
    start.setDate(today.getDate() - days);
    end.setDate(today.getDate() + 1);

    const startDateStr = start.toISOString().split("T")[0];
    const endDateStr = end.toISOString().split("T")[0];
    setLocalStartDate(startDateStr);
    setLocalEndDate(endDateStr);
    if (onDateFilterChange) {
      onDateFilterChange(startDateStr, endDateStr);
    }
  };

  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <div className="transactions-container">
      {/* Date Filter */}
      <div
        className="date-filter-container"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          paddingBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        {/* Search Input */}
        <div style={{ flexShrink: 0, position: "relative" }}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
                fontSize: "1rem",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              🔍
            </div>
            <input
              name="search-transaction"
              type="text"
              placeholder="Search invoice or transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: ".75rem 1rem .75rem 2.5rem",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "0.875rem",
                width: "280px",
                outline: "none",
                transition: "all 0.2s ease",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
              }}
            />
          </div>
        </div>

        {/*Date Range Filter*/}
        {false && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexShrink: 0,
            }}
          >
            <input
              type="date"
              name="fromDate"
              value={localStartDate}
              onChange={(e) => {
                setLocalStartDate(e.target.value);
                setSelectedPeriod("Custom");
              }}
              style={{
                padding: "0.5rem",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "0.875rem",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>to</span>
            <input
              type="date"
              name="toDate"
              value={localEndDate}
              onChange={(e) => {
                setLocalEndDate(e.target.value);
                setSelectedPeriod("Custom");
              }}
              style={{
                padding: "0.5rem",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "0.875rem",
              }}
            />
            <button
              onClick={handleDateFilterApply}
              className="wide-dash-border-btn"
              disabled={loading}
              style={{
                marginLeft: "0.5rem",
                whiteSpace: "nowrap",
              }}
            >
              Apply Filter
            </button>
          </div>
        )}
        {/*Date Period Filter*/}
        <div style={{ flexShrink: 0 }}>
          <DatePeriodButtons
            selectedPeriod={selectedPeriod}
            loading={loading}
            onPeriodChange={handlePeriodChange}
          />
        </div>
      </div>

      {/* Filter Buttons */}
      <div
        className="filter-buttons"
        style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: "1rem" }}
      >
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          className={`filter-btn ${filter === saleFilterValue ? "active" : ""}`}
          onClick={() => setFilter(saleFilterValue)}
        >
          Sale
        </button>
        <button
          className={`filter-btn ${
            filter === refundFilterValue ? "active" : ""
          }`}
          onClick={() => setFilter(refundFilterValue)}
        >
          Refund
        </button>
      </div>
      {/* Transactions List */}
      <div className="transactions-list">
        <div className="user-list-header">
          <h3>Transactions ({filteredTransactions.length})</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="wide-dash-border-btn"
              onClick={onRefresh}
              title="Refresh Transactions"
              disabled={loading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <span>↻</span>
              <span
                style={{
                  display: isMobile ? "none" : "inline",
                }}
              >
                Refresh
              </span>
            </button>
            <button
              className="wide-dash-border-btn"
              onClick={onAddTransaction}
              title="Add Transaction"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <span>➕</span>
              <span
                style={{
                  display: isMobile ? "none" : "inline",
                }}
              >
                Add Transaction
              </span>
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="payment-history-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Method</th>
                <th>Amount</th>
                {!isMobile && <th>Notes</th>}
                <th style={{ textAlign: "right" }}>
                  {!isMobile ? "Actions" : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={isMobile ? 4 : 5}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    Loading transactions...
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan={isMobile ? 4 : 5}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    No transactions found
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <div>
                        {transaction.invoice}
                        <span
                          className="transaction-request-origin"
                          style={{
                            marginLeft: ".25rem",
                            marginRight: ".25rem",
                            padding: "0.25rem 0.25rem",
                            fontSize: "0.55rem",
                            fontWeight: "500",
                            background: "#f0fdf4",
                            color: "#374151",
                            border: "1px solid #d1d5db",
                            textTransform: "uppercase",
                            borderRadius: "4px",
                          }}
                        >
                          {transaction.requestOrigin}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#666",
                          marginTop: "0.25rem",
                        }}
                      >
                        {new Date(transaction.date).toLocaleString()}
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#6b7280",
                            marginTop: "0.25rem",
                          }}
                        >
                          {transaction.accountIdentifier}
                          {" | "}
                          {transaction.terminalIdentifier
                            ? transaction.terminalIdentifier.length > 15
                              ? transaction.terminalIdentifier.substring(
                                  0,
                                  15
                                ) + "..."
                              : transaction.terminalIdentifier
                            : "Not specified"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        {transaction.card}
                        <span
                          style={{
                            marginLeft: ".25rem",
                            padding: "0.125rem 0.25rem",
                            borderRadius: "4px",
                            fontSize: "0.55rem",
                            fontWeight: "500",
                            background: "#dbeafe",
                            color: "#1e40af",
                            marginRight: "0.5rem",
                          }}
                        >
                          {transaction.entryType}
                        </span>
                        <span
                          className={`terminal-status-badge ${
                            transaction.status.toLowerCase() === "success"
                              ? "status-active"
                              : transaction.status.toLowerCase() === "fail"
                              ? "status-inactive"
                              : "status-pending"
                          }`}
                          style={{
                            fontSize: "0.55rem",
                            padding: "0.125rem 0.25rem",
                          }}
                        >
                          {transaction.status}
                        </span>
                      </div>
                      {transaction.transactionId && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#9ca3af",
                            marginTop: "0.25rem;",
                          }}
                        >
                          {transaction.transactionId}
                        </div>
                      )}
                      {!transaction.transactionId && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#9ca3af",
                            marginTop: "0.25rem",
                          }}
                        >
                          {transaction.id
                            ? transaction.id.length > 15
                              ? "..." +
                                transaction.id.slice(15).replace(/-/g, "")
                              : transaction.id
                            : "NA"}
                        </div>
                      )}
                      <div>
                        {transaction.authCode && (
                          <span
                            style={{ fontSize: "0.75rem", color: "#9ca3af" }}
                          >
                            {transaction.authCode} {" | "} {transaction.account}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <span
                          className={`transaction-amount ${transaction.transType}`}
                          style={{
                            fontWeight: "600",
                            color:
                              transaction.transType === saleFilterValue
                                ? "#10b981"
                                : "#ef4444",
                          }}
                        >
                          {transaction.transType === saleFilterValue
                            ? "+"
                            : "-"}
                          ${Math.abs(transaction.amount).toFixed(2)}
                        </span>
                        {transaction.tip > 0 && (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#6b7280",
                              marginTop: "0.25rem",
                            }}
                          >
                            Tip: ${transaction.tip.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </td>
                    {!isMobile && (
                      <td>
                        {/*<strong>{transaction.description}</strong>*/}
                        {transaction.errorText}
                      </td>
                    )}
                    <td style={{ textAlign: "right", padding: "1rem" }}>
                      <button
                        className="btn-icon"
                        onClick={() => handleViewDetails(transaction)}
                        title="View Details"
                        style={{
                          color: "#0369a1",
                          padding: "0.4rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          transition: "all 0.2s ease",
                          float: "right",
                        }}
                        onMouseEnter={(e) => {
                          //e.currentTarget.style.background = "#0284c7";
                          e.currentTarget.style.fontWeight = "bold";
                        }}
                        onMouseLeave={(e) => {
                          //e.currentTarget.style.background = "#e0f2fe";
                          e.currentTarget.style.fontWeight = "normal";
                        }}
                      >
                        ❯
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Details Modal */}
      <TransactionDetails
        isOpen={isDetailsOpen}
        transaction={selectedTransaction}
        onClose={handleCloseDetails}
      />
    </div>
  );
};

export default TransactionList;
